import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, MenuItem, TextField,
  Button, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Stack, Alert, Paper, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, Tab, Tabs, InputAdornment,
  FormControlLabel, Switch, Checkbox, FormGroup, FormLabel, Autocomplete
} from '@mui/material';
import {
  School, Add, Edit, Delete, Search, Refresh,
  CheckCircle, Warning, MonetizationOn, People,
  FileDownload, Gavel, Print, VolunteerActivism,
  Autorenew, HowToReg, BarChart, Article
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { settingsAPI } from '../services/api';
import { exportCSV } from '../utils/export';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api' });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('sms_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const FUNDER_TYPES  = ['government','ngo','school','church','donor','corporate','other'];
const PROG_TYPES    = ['full_bursary','partial_bursary','scholarship','loan','sponsorship'];
const COV_TYPES     = [
  { value: 'percentage',    label: 'Percentage of fees' },
  { value: 'fixed_amount',  label: 'Fixed amount (E)' },
  { value: 'specific_fees', label: 'Full coverage of specific fee types' },
];
const FEE_TYPES = [
  'tuition','exam','sports','boarding','registration',
  'school_trip','invigilation','uniform','books_stationery','library','subject_fee','other'
];
const AWARD_STATUSES = ['active','suspended','cancelled','completed','pending_renewal'];
const APP_STATUSES   = ['pending','approved','rejected','waitlisted'];

const fmt  = n => `E${parseFloat(n || 0).toLocaleString('en', { minimumFractionDigits: 2 })}`;
const fmtD = d => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const ptLabel = v => (v || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ── Overview Tab ───────────────────────────────────────────────────────────────
function OverviewTab({ academicYears }) {
  const { enqueueSnackbar } = useSnackbar();
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setYear] = useState('');

  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);
  useEffect(() => {
    if (!selectedYear) return;
    setLoading(true);
    api.get('/bursary/stats', { params: { academic_year_id: selectedYear } })
      .then(r => { if (r.data.success) setStats(r.data.data); })
      .catch(() => enqueueSnackbar('Failed to load stats', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [selectedYear, enqueueSnackbar]);

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField select size="small" label="Academic Year" value={selectedYear} onChange={e => setYear(e.target.value)} sx={{ minWidth: 200 }}>
          {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
        </TextField>
      </Box>
      {loading ? <CircularProgress /> : stats && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Active Awards',       value: stats.summary?.active_awards || 0,         color: '#2e7d32' },
              { label: 'Students Supported',  value: stats.summary?.students_supported || 0,    color: '#0f2b4a' },
              { label: 'Total Disbursed',     value: fmt(stats.summary?.total_disbursed),        color: '#c9952a' },
              { label: 'Pending Applications',value: stats.summary?.pending_applications || 0,  color: '#1565c0' },
              { label: 'Renewals Due (30d)',  value: stats.summary?.renewals_due || 0,           color: '#c62828' },
            ].map(s => (
              <Grid item xs={6} sm key={s.label}>
                <Card variant="outlined" sx={{ borderRadius: 2, borderLeft: `4px solid ${s.color}` }}>
                  <CardContent sx={{ pb: '12px !important' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" fontSize={9}>{s.label}</Typography>
                    <Typography variant="h5" fontWeight={800} sx={{ color: s.color }}>{s.value}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography fontWeight={700} gutterBottom>📊 Awards by Programme</Typography>
                  {!stats.by_programme?.length && <Typography fontSize={12} color="text.secondary">No active awards yet</Typography>}
                  {stats.by_programme?.map((p, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.8, borderBottom: '1px solid #f0f0f0' }}>
                      <Box>
                        <Typography fontSize={13} fontWeight={600}>{p.programme_name}</Typography>
                        <Typography fontSize={11} color="text.secondary">{p.funder_name || 'School Fund'} · {ptLabel(p.programme_type)}</Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        <Chip label={`${p.recipients} students`} size="small" color="primary" sx={{ fontSize: 10 }} />
                        <Chip label={fmt(p.disbursed)} size="small" color="warning" sx={{ fontSize: 10 }} />
                      </Stack>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card variant="outlined" sx={{ borderRadius: 2, border: stats.renewals_due?.length ? '1px solid #fca5a5' : undefined }}>
                <CardContent>
                  <Typography fontWeight={700} gutterBottom color={stats.renewals_due?.length ? 'error.main' : 'text.primary'}>
                    🔄 Upcoming Renewals
                  </Typography>
                  {!stats.renewals_due?.length && <Typography fontSize={12} color="success.main">No renewals due in the next 30 days</Typography>}
                  {stats.renewals_due?.map((r, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                      <Box>
                        <Typography fontSize={12} fontWeight={600}>{r.first_name} {r.last_name}</Typography>
                        <Typography fontSize={11} color="text.secondary">{r.admission_number} · {r.programme_name}</Typography>
                      </Box>
                      <Chip label={`Due ${fmtD(r.renewal_due)}`} size="small" color="warning" sx={{ fontSize: 10 }} />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}

// ── Programmes Tab ─────────────────────────────────────────────────────────────
function ProgrammesTab({ academicYears }) {
  const { enqueueSnackbar } = useSnackbar();
  const [programmes, setProgs] = useState([]);
  const [funders, setFunders]  = useState([]);
  const [loading, setLoading]  = useState(false);
  const [dialog, setDialog]    = useState(false);
  const [editing, setEditing]  = useState(null);
  const [funderDlg, setFunderDlg] = useState(false);
  const BLANK_PROG = { name:'', funder_id:'', programme_type:'partial_bursary', coverage_type:'percentage',
    coverage_value:'', fee_types_covered:[], academic_year_id:'', eligibility_criteria:'',
    requires_means_test:false, requires_application:true, max_recipients:'', renewable:true, renewal_conditions:'', notes:'' };
  const BLANK_FUND = { name:'', funder_type:'government', contact_name:'', contact_email:'', contact_phone:'', notes:'' };
  const [progForm, setProgForm] = useState(BLANK_PROG);
  const [funderForm, setFundForm] = useState(BLANK_FUND);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pr, fr] = await Promise.all([api.get('/bursary/programmes'), api.get('/bursary/funders')]);
      if (pr.data.success) setProgs(pr.data.data);
      if (fr.data.success) setFunders(fr.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const openEdit = p => {
    setEditing(p);
    let covered = [];
    try { covered = JSON.parse(p.fee_types_covered || '[]'); } catch {}
    setProgForm({ ...p, fee_types_covered: covered || [], requires_means_test: !!p.requires_means_test, requires_application: !!p.requires_application, renewable: !!p.renewable });
    setDialog(true);
  };

  const saveProg = async () => {
    try {
      if (editing) { await api.put(`/bursary/programmes/${editing.id}`, { ...progForm, is_active: 1 }); }
      else { await api.post('/bursary/programmes', progForm); }
      enqueueSnackbar(editing ? 'Updated' : 'Programme created', { variant: 'success' });
      setDialog(false); setEditing(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const saveFunder = async () => {
    try {
      await api.post('/bursary/funders', funderForm);
      enqueueSnackbar('Funder added', { variant: 'success' });
      setFunderDlg(false); load();
    } catch (err) { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  const toggleFeeType = val => setProgForm(f => ({
    ...f, fee_types_covered: f.fee_types_covered?.includes(val)
      ? f.fee_types_covered.filter(x => x !== val)
      : [...(f.fee_types_covered || []), val]
  }));

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
        <Chip label={`${programmes.length} programme(s)`} size="small" />
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => setFunderDlg(true)}>Add Funder</Button>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditing(null); setProgForm({...BLANK_PROG}); setDialog(true); }} sx={{ bgcolor: '#0f2b4a' }}>
          New Programme
        </Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Grid container spacing={2}>
          {programmes.length === 0 && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
                <School sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">No programmes yet. Create your first bursary programme.</Typography>
              </Paper>
            </Grid>
          )}
          {programmes.map(p => (
            <Grid item xs={12} sm={6} md={4} key={p.id}>
              <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                    <Box sx={{ flexGrow: 1, mr: 1 }}>
                      <Typography fontWeight={700} fontSize={14}>{p.name}</Typography>
                      <Typography fontSize={11} color="text.secondary">{p.funder_name || 'School Fund'}</Typography>
                    </Box>
                    <IconButton size="small" onClick={() => openEdit(p)}><Edit fontSize="small" /></IconButton>
                  </Stack>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1 }}>
                    <Chip label={ptLabel(p.programme_type)} size="small" color="primary" sx={{ fontSize: 10 }} />
                    <Chip label={p.coverage_type === 'percentage' ? `${p.coverage_value}%` : fmt(p.coverage_value)} size="small" color="success" sx={{ fontSize: 10 }} />
                    {p.renewable && <Chip label="Renewable" size="small" icon={<Autorenew sx={{ fontSize: 12 }} />} sx={{ fontSize: 10 }} />}
                  </Stack>
                  <Grid container spacing={1}>
                    <Grid item xs={6}><Typography fontSize={10} color="text.secondary">Recipients</Typography><Typography fontWeight={700} fontSize={13}>{p.active_recipients}</Typography></Grid>
                    <Grid item xs={6}><Typography fontSize={10} color="text.secondary">Disbursed</Typography><Typography fontWeight={700} fontSize={13}>{fmt(p.total_disbursed)}</Typography></Grid>
                  </Grid>
                  {p.max_recipients && <Typography fontSize={10} color="text.secondary" sx={{ mt: 0.5 }}>Max {p.max_recipients} recipients</Typography>}
                  {p.eligibility_criteria && <Typography fontSize={11} color="text.secondary" sx={{ mt: 0.5 }} noWrap>{p.eligibility_criteria}</Typography>}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Programme Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#0f2b4a', color: 'white' }}>{editing ? 'Edit Programme' : 'New Bursary Programme'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Programme Name *" value={progForm.name} onChange={e => setProgForm(f => ({ ...f, name: e.target.value }))} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField select fullWidth size="small" label="Funding Body" value={progForm.funder_id} onChange={e => setProgForm(f => ({ ...f, funder_id: e.target.value }))}>
                <MenuItem value="">No specific funder</MenuItem>
                {funders.map(f => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField select fullWidth size="small" label="Programme Type" value={progForm.programme_type} onChange={e => setProgForm(f => ({ ...f, programme_type: e.target.value }))}>
                {PROG_TYPES.map(t => <MenuItem key={t} value={t}>{ptLabel(t)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth size="small" label="Tied to Year (optional)" value={progForm.academic_year_id} onChange={e => setProgForm(f => ({ ...f, academic_year_id: e.target.value }))}>
                <MenuItem value="">All years (recurring)</MenuItem>
                {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField select fullWidth size="small" label="Coverage Type *" value={progForm.coverage_type} onChange={e => setProgForm(f => ({ ...f, coverage_type: e.target.value }))}>
                {COV_TYPES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
              </TextField>
            </Grid>
            {progForm.coverage_type !== 'specific_fees' && (
              <Grid item xs={6} sm={4}>
                <TextField fullWidth size="small" type="number" label={progForm.coverage_type === 'percentage' ? 'Coverage % *' : 'Fixed Amount (E) *'}
                  value={progForm.coverage_value} onChange={e => setProgForm(f => ({ ...f, coverage_value: e.target.value }))}
                  InputProps={progForm.coverage_type === 'percentage'
                    ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                    : { startAdornment: <InputAdornment position="start">E</InputAdornment> }}
                  inputProps={{ min: 0, max: progForm.coverage_type === 'percentage' ? 100 : undefined }} />
              </Grid>
            )}
            <Grid item xs={6} sm={4}>
              <TextField fullWidth size="small" type="number" label="Max Recipients (blank = unlimited)" value={progForm.max_recipients}
                onChange={e => setProgForm(f => ({ ...f, max_recipients: e.target.value }))} />
            </Grid>

            {/* Fee types covered */}
            <Grid item xs={12}>
              <FormLabel sx={{ fontSize: 12, fontWeight: 700 }}>Fee Types Covered (leave all unchecked = all fees)</FormLabel>
              <FormGroup row sx={{ mt: 0.5 }}>
                {FEE_TYPES.map(ft => (
                  <FormControlLabel key={ft} control={
                    <Checkbox size="small" checked={progForm.fee_types_covered?.includes(ft) || false}
                      onChange={() => toggleFeeType(ft)} />
                  } label={<Typography fontSize={12}>{ptLabel(ft)}</Typography>} />
                ))}
              </FormGroup>
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Eligibility Criteria" value={progForm.eligibility_criteria}
                onChange={e => setProgForm(f => ({ ...f, eligibility_criteria: e.target.value }))}
                placeholder="e.g. Household income below E20,000 per year, academic average above 60%" multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={3}>
                <FormControlLabel control={<Switch size="small" checked={!!progForm.requires_means_test} onChange={e => setProgForm(f => ({ ...f, requires_means_test: e.target.checked }))} />}
                  label={<Typography fontSize={13}>Requires means test</Typography>} />
                <FormControlLabel control={<Switch size="small" checked={!!progForm.requires_application} onChange={e => setProgForm(f => ({ ...f, requires_application: e.target.checked }))} />}
                  label={<Typography fontSize={13}>Requires application</Typography>} />
                <FormControlLabel control={<Switch size="small" checked={!!progForm.renewable} onChange={e => setProgForm(f => ({ ...f, renewable: e.target.checked }))} />}
                  label={<Typography fontSize={13}>Renewable each year</Typography>} />
              </Stack>
            </Grid>
            {progForm.renewable && (
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Renewal Conditions" value={progForm.renewal_conditions}
                  onChange={e => setProgForm(f => ({ ...f, renewal_conditions: e.target.value }))}
                  placeholder="e.g. Maintain academic average of 60%, submit updated means test annually" />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes" value={progForm.notes} onChange={e => setProgForm(f => ({ ...f, notes: e.target.value }))} multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveProg} sx={{ bgcolor: '#0f2b4a' }}>{editing ? 'Update' : 'Create Programme'}</Button>
        </DialogActions>
      </Dialog>

      {/* Add Funder Dialog */}
      <Dialog open={funderDlg} onClose={() => setFunderDlg(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Funding Body</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={8}><TextField fullWidth size="small" label="Funder Name *" value={funderForm.name} onChange={e => setFundForm(f => ({ ...f, name: e.target.value }))} /></Grid>
            <Grid item xs={4}>
              <TextField select fullWidth size="small" label="Type" value={funderForm.funder_type} onChange={e => setFundForm(f => ({ ...f, funder_type: e.target.value }))}>
                {FUNDER_TYPES.map(t => <MenuItem key={t} value={t}>{ptLabel(t)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Contact Name" value={funderForm.contact_name} onChange={e => setFundForm(f => ({ ...f, contact_name: e.target.value }))} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Contact Phone" value={funderForm.contact_phone} onChange={e => setFundForm(f => ({ ...f, contact_phone: e.target.value }))} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Contact Email" value={funderForm.contact_email} onChange={e => setFundForm(f => ({ ...f, contact_email: e.target.value }))} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Notes" value={funderForm.notes} onChange={e => setFundForm(f => ({ ...f, notes: e.target.value }))} multiline rows={2} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFunderDlg(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveFunder} sx={{ bgcolor: '#0f2b4a' }}>Add Funder</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Awards Tab ─────────────────────────────────────────────────────────────────
function AwardsTab({ academicYears, students }) {
  const { enqueueSnackbar } = useSnackbar();
  const [awards, setAwards]       = useState([]);
  const [programmes, setProgs]    = useState([]);
  const [loading, setLoading]     = useState(false);
  const [selectedYear, setYear]   = useState('');
  const [filterProg, setProg]     = useState('');
  const [filterStatus, setStatus] = useState('active');
  const [dialog, setDialog]       = useState(false);
  const [editing, setEditing]     = useState(null);
  const [applying, setApplying]   = useState(null);
  const BLANK = {
    programme_id:'', student_id:'', academic_year_id:'',
    award_date: new Date().toISOString().split('T')[0],
    start_date:'', end_date:'', coverage_type:'percentage', coverage_value:'',
    fee_types_covered:[], means_test_income:'', means_test_notes:'',
    award_letter_notes:'', renewal_due:'', notes:'', apply_immediately: true
  };
  const [form, setForm] = useState(BLANK);

  const load = useCallback(async () => {
    if (!selectedYear) return;
    setLoading(true);
    try {
      const params = { academic_year_id: selectedYear };
      if (filterProg)   params.programme_id = filterProg;
      if (filterStatus) params.status = filterStatus;
      const [ar, pr] = await Promise.all([
        api.get('/bursary/awards', { params }),
        api.get('/bursary/programmes')
      ]);
      if (ar.data.success) setAwards(ar.data.data);
      if (pr.data.success) setProgs(pr.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [selectedYear, filterProg, filterStatus, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...BLANK, academic_year_id: selectedYear });
    // Pre-fill coverage from selected programme
    setDialog(true);
  };

  const onProgChange = (progId) => {
    const prog = programmes.find(p => p.id == progId);
    if (prog) {
      let covered = [];
      try { covered = JSON.parse(prog.fee_types_covered || '[]'); } catch {}
      setForm(f => ({ ...f, programme_id: progId, coverage_type: prog.coverage_type, coverage_value: prog.coverage_value, fee_types_covered: covered }));
    } else {
      setForm(f => ({ ...f, programme_id: progId }));
    }
  };

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/bursary/awards/${editing.id}`, form);
        enqueueSnackbar('Award updated', { variant: 'success' });
      } else {
        const r = await api.post('/bursary/awards', form);
        enqueueSnackbar(r.data.message, { variant: 'success' });
      }
      setDialog(false); setEditing(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const applyDiscount = async (award) => {
    setApplying(award.id);
    try {
      const r = await api.post(`/bursary/awards/${award.id}/apply`);
      enqueueSnackbar(r.data.message, { variant: 'success' });
      load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
    finally { setApplying(null); }
  };

  const printLetter = (id) => {
    const token = localStorage.getItem('sms_token');
    window.open(`${API_BASE}/bursary/awards/${id}/letter?token=${token}`, '_blank');
  };

  const del = async (id) => {
    if (!window.confirm('Delete this award? This cannot be undone.')) return;
    try { await api.delete(`/bursary/awards/${id}`); enqueueSnackbar('Deleted', { variant: 'success' }); load(); }
    catch (err) { enqueueSnackbar(err.response?.data?.message || 'Cannot delete', { variant: 'error' }); }
  };

  const toggleFeeType = val => setForm(f => ({
    ...f, fee_types_covered: f.fee_types_covered?.includes(val)
      ? f.fee_types_covered.filter(x => x !== val)
      : [...(f.fee_types_covered || []), val]
  }));

  const selectedProg = programmes.find(p => p.id == form.programme_id);

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={3}>
          <TextField select size="small" fullWidth label="Academic Year" value={selectedYear} onChange={e => setYear(e.target.value)}>
            {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField select size="small" fullWidth label="Programme" value={filterProg} onChange={e => setProg(e.target.value)}>
            <MenuItem value="">All programmes</MenuItem>
            {programmes.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField select size="small" fullWidth label="Status" value={filterStatus} onChange={e => setStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {AWARD_STATUSES.map(s => <MenuItem key={s} value={s}>{ptLabel(s)}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
          <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={() =>
            exportCSV('bursary_awards', ['Student','Admission','Class','Programme','Type','Coverage','Status','Disbursed','Renewal'],
              awards.map(a => [`${a.first_name} ${a.last_name}`, a.admission_number, a.class_name||'',
                a.programme_name, a.funder_name||'', `${a.coverage_value}${a.coverage_type==='percentage'?'%':'E'}`,
                a.status, fmt(a.total_awarded), fmtD(a.renewal_due)]))
          }>Export</Button>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate} sx={{ bgcolor: '#0f2b4a' }}>Grant Award</Button>
        </Grid>
      </Grid>

      {loading ? <CircularProgress /> : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Student','Class','Programme / Funder','Coverage','Status','Disbursed','Renewal','Actions'].map(h => (
                    <TableCell key={h} sx={{ bgcolor: '#0f2b4a', color: 'white', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {awards.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No awards found</TableCell></TableRow>}
                {awards.map((a, i) => (
                  <TableRow key={a.id} hover sx={{ bgcolor: a.status === 'pending_renewal' ? '#fffde7' : i % 2 === 0 ? 'white' : '#fafafa' }}>
                    <TableCell>
                      <Typography fontSize={12} fontWeight={600}>{a.last_name}, {a.first_name}</Typography>
                      <Typography fontSize={10} color="text.secondary">{a.admission_number}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{a.class_name || '—'}</TableCell>
                    <TableCell>
                      <Typography fontSize={12} fontWeight={600}>{a.programme_name}</Typography>
                      <Typography fontSize={10} color="text.secondary">{a.funder_name || 'School Fund'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color="success" sx={{ fontSize: 10, fontWeight: 700 }}
                        label={a.coverage_type === 'percentage' ? `${a.coverage_value}%` : fmt(a.coverage_value)} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={ptLabel(a.status)}
                        color={a.status === 'active' ? 'success' : a.status === 'pending_renewal' ? 'warning' : 'default'}
                        sx={{ fontSize: 10 }} />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#c9952a' }}>{fmt(a.total_awarded)}</TableCell>
                    <TableCell sx={{ fontSize: 11, color: a.renewal_due && new Date(a.renewal_due) < new Date() ? 'error.main' : 'text.secondary' }}>
                      {fmtD(a.renewal_due)}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.3}>
                        <Tooltip title="Apply discount to fee invoices">
                          <span>
                            <IconButton size="small" color="success" onClick={() => applyDiscount(a)}
                              disabled={applying === a.id || a.status !== 'active'}>
                              {applying === a.id ? <CircularProgress size={14} /> : <MonetizationOn fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Print award letter">
                          <IconButton size="small" color="primary" onClick={() => printLetter(a.id)}>
                            <Print fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => { setEditing(a); setForm({ ...a, fee_types_covered: (() => { try { return JSON.parse(a.fee_types_covered||'[]'); } catch { return []; } })(), apply_immediately: false, start_date: a.start_date?.split('T')[0]||'', end_date: a.end_date?.split('T')[0]||'', renewal_due: a.renewal_due?.split('T')[0]||'' }); setDialog(true); }}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => del(a.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Award Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#0f2b4a', color: 'white' }}>{editing ? 'Edit Award' : 'Grant Bursary / Scholarship Award'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {!editing && <>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth size="small" label="Programme *" value={form.programme_id} onChange={e => onProgChange(e.target.value)}>
                  <MenuItem value="">Select programme…</MenuItem>
                  {programmes.map(p => <MenuItem key={p.id} value={p.id}>{p.name} ({ptLabel(p.programme_type)})</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete size="small" options={students}
                  getOptionLabel={s => `${s.first_name} ${s.last_name} (${s.admission_number})`}
                  onChange={(_, v) => setForm(f => ({ ...f, student_id: v?.id || '' }))}
                  renderInput={p => <TextField {...p} label="Student *" />} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField select fullWidth size="small" label="Academic Year *" value={form.academic_year_id} onChange={e => setForm(f => ({ ...f, academic_year_id: e.target.value }))}>
                  {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
                </TextField>
              </Grid>
            </>}

            <Grid item xs={6} sm={4}>
              <TextField select fullWidth size="small" label="Coverage Type *" value={form.coverage_type} onChange={e => setForm(f => ({ ...f, coverage_type: e.target.value }))}>
                {COV_TYPES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
              </TextField>
            </Grid>
            {form.coverage_type !== 'specific_fees' && (
              <Grid item xs={6} sm={4}>
                <TextField fullWidth size="small" type="number" label={form.coverage_type === 'percentage' ? 'Coverage % *' : 'Amount (E) *'}
                  value={form.coverage_value} onChange={e => setForm(f => ({ ...f, coverage_value: e.target.value }))}
                  InputProps={form.coverage_type === 'percentage' ? { endAdornment: <InputAdornment position="end">%</InputAdornment> } : { startAdornment: <InputAdornment position="start">E</InputAdornment> }} />
              </Grid>
            )}
            <Grid item xs={6} sm={4}>
              <TextField fullWidth size="small" type="date" label="Award Date *" value={form.award_date} onChange={e => setForm(f => ({ ...f, award_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth size="small" type="date" label="Start Date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth size="small" type="date" label="End Date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth size="small" type="date" label="Renewal Due Date" value={form.renewal_due} onChange={e => setForm(f => ({ ...f, renewal_due: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>

            {/* Fee types override */}
            <Grid item xs={12}>
              <FormLabel sx={{ fontSize: 12, fontWeight: 700 }}>Fee Types Covered (blank = all fees)</FormLabel>
              <FormGroup row sx={{ mt: 0.5 }}>
                {FEE_TYPES.map(ft => (
                  <FormControlLabel key={ft} control={
                    <Checkbox size="small" checked={form.fee_types_covered?.includes(ft) || false} onChange={() => toggleFeeType(ft)} />
                  } label={<Typography fontSize={12}>{ptLabel(ft)}</Typography>} />
                ))}
              </FormGroup>
            </Grid>

            {selectedProg?.requires_means_test && (
              <>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" type="number" label="Household Income (E/year)" value={form.means_test_income} onChange={e => setForm(f => ({ ...f, means_test_income: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start">E</InputAdornment> }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="Means Test Notes" value={form.means_test_notes} onChange={e => setForm(f => ({ ...f, means_test_notes: e.target.value }))} />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Award Letter Notes (appears on printed letter)" value={form.award_letter_notes}
                onChange={e => setForm(f => ({ ...f, award_letter_notes: e.target.value }))} multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Internal Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} multiline rows={2} />
            </Grid>

            {!editing && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: form.apply_immediately ? '#f0fdf4' : 'white', border: `1px solid ${form.apply_immediately ? '#2e7d32' : '#ddd'}` }}>
                  <FormControlLabel
                    control={<Switch checked={!!form.apply_immediately} onChange={e => setForm(f => ({ ...f, apply_immediately: e.target.checked }))} />}
                    label={<Box>
                      <Typography fontSize={13} fontWeight={600}>Apply discount to fee invoices immediately</Typography>
                      <Typography fontSize={11} color="text.secondary">Automatically reduces the student's outstanding fee invoices right now. You can also do this manually later.</Typography>
                    </Box>}
                  />
                </Paper>
              </Grid>
            )}

            {editing && (
              <Grid item xs={12}>
                <TextField select fullWidth size="small" label="Award Status" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  {AWARD_STATUSES.map(s => <MenuItem key={s} value={s}>{ptLabel(s)}</MenuItem>)}
                </TextField>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} startIcon={<CheckCircle />} sx={{ bgcolor: '#0f2b4a' }}>
            {editing ? 'Update Award' : 'Grant Award'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Applications Tab ───────────────────────────────────────────────────────────
function ApplicationsTab({ academicYears, students }) {
  const { enqueueSnackbar } = useSnackbar();
  const [apps, setApps]         = useState([]);
  const [programmes, setProgs]  = useState([]);
  const [loading, setLoading]   = useState(false);
  const [selectedYear, setYear] = useState('');
  const [filterStatus, setStatus] = useState('pending');
  const [dialog, setDialog]     = useState(false);
  const BLANK = { programme_id:'', student_id:'', academic_year_id:'', applied_date: new Date().toISOString().split('T')[0], household_income:'', application_notes:'' };
  const [form, setForm]         = useState(BLANK);

  const load = useCallback(async () => {
    if (!selectedYear) return;
    setLoading(true);
    try {
      const [ar, pr] = await Promise.all([
        api.get('/bursary/applications', { params: { academic_year_id: selectedYear, status: filterStatus || undefined } }),
        api.get('/bursary/programmes')
      ]);
      if (ar.data.success) setApps(ar.data.data);
      if (pr.data.success) setProgs(pr.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [selectedYear, filterStatus, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);

  const submit = async () => {
    try {
      await api.post('/bursary/applications', { ...form, academic_year_id: selectedYear });
      enqueueSnackbar('Application submitted', { variant: 'success' });
      setDialog(false); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const review = async (id, status, reason = null) => {
    try {
      await api.put(`/bursary/applications/${id}/review`, { status, rejection_reason: reason });
      enqueueSnackbar(`Application ${status}`, { variant: 'success' });
      load();
    } catch { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
        <TextField select size="small" label="Year" value={selectedYear} onChange={e => setYear(e.target.value)} sx={{ minWidth: 180 }}>
          {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Status" value={filterStatus} onChange={e => setStatus(e.target.value)} sx={{ width: 140 }}>
          <MenuItem value="">All</MenuItem>
          {APP_STATUSES.map(s => <MenuItem key={s} value={s}>{ptLabel(s)}</MenuItem>)}
        </TextField>
        <Chip label={`${apps.filter(a => a.status === 'pending').length} pending`} size="small" color="warning" />
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" startIcon={<Add />} onClick={() => { setForm({ ...BLANK, academic_year_id: selectedYear }); setDialog(true); }} sx={{ bgcolor: '#0f2b4a' }}>
          Log Application
        </Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <TableContainer sx={{ maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Student','Class','Programme','Applied','Income','Status','Actions'].map(h => (
                    <TableCell key={h} sx={{ bgcolor: '#0f2b4a', color: 'white', fontWeight: 700, fontSize: 11 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {apps.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No applications found</TableCell></TableRow>}
                {apps.map((a, i) => (
                  <TableRow key={a.id} hover sx={{ bgcolor: a.status === 'pending' ? '#fffde7' : i % 2 === 0 ? 'white' : '#fafafa' }}>
                    <TableCell>
                      <Typography fontSize={12} fontWeight={600}>{a.last_name}, {a.first_name}</Typography>
                      <Typography fontSize={10} color="text.secondary">{a.admission_number}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{a.class_name || '—'}</TableCell>
                    <TableCell><Typography fontSize={12}>{a.programme_name}</Typography><Typography fontSize={10} color="text.secondary">{a.funder_name}</Typography></TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{fmtD(a.applied_date)}</TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{a.household_income ? fmt(a.household_income) : '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={ptLabel(a.status)}
                        color={a.status === 'approved' ? 'success' : a.status === 'rejected' ? 'error' : a.status === 'pending' ? 'warning' : 'default'}
                        sx={{ fontSize: 10, fontWeight: 700 }} />
                    </TableCell>
                    <TableCell>
                      {a.status === 'pending' && (
                        <Stack direction="row" spacing={0.5}>
                          <Button size="small" variant="contained" color="success" onClick={() => review(a.id, 'approved')} sx={{ fontSize: 10 }}>Approve</Button>
                          <Button size="small" variant="outlined" color="error" onClick={() => {
                            const reason = window.prompt('Reason for rejection:');
                            if (reason !== null) review(a.id, 'rejected', reason);
                          }} sx={{ fontSize: 10 }}>Reject</Button>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Log Bursary Application</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete size="small" options={students}
                getOptionLabel={s => `${s.first_name} ${s.last_name} (${s.admission_number})`}
                onChange={(_, v) => setForm(f => ({ ...f, student_id: v?.id || '' }))}
                renderInput={p => <TextField {...p} label="Student *" />} />
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Programme *" value={form.programme_id} onChange={e => setForm(f => ({ ...f, programme_id: e.target.value }))}>
                <MenuItem value="">Select programme…</MenuItem>
                {programmes.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="date" label="Applied Date" value={form.applied_date} onChange={e => setForm(f => ({ ...f, applied_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Household Income (E/yr)" value={form.household_income} onChange={e => setForm(f => ({ ...f, household_income: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start">E</InputAdornment> }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Application Notes" value={form.application_notes} onChange={e => setForm(f => ({ ...f, application_notes: e.target.value }))} multiline rows={3} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={submit} sx={{ bgcolor: '#0f2b4a' }}>Submit Application</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function Bursaries() {
  const [tab, setTab]           = useState(0);
  const [academicYears, setAY]  = useState([]);
  const [students, setStudents] = useState([]);

  useEffect(() => {
    settingsAPI.getAcademicYears().then(r => { if (r.data.success) setAY(r.data.data); }).catch(() => {});
    import('../services/api').then(({ studentsAPI }) => {
      studentsAPI.getAll({ limit: 2000, status: 'active' }).then(r => { if (r.data.success) setStudents(r.data.data); }).catch(() => {});
    });
  }, []);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <School color="primary" /> Bursaries & Scholarships
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Funding bodies · Programmes · Awards · Fee integration · Award letters
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<BarChart fontSize="small" />}        label="Overview"       iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<VolunteerActivism fontSize="small" />} label="Programmes"   iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<MonetizationOn fontSize="small" />}  label="Awards"         iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<HowToReg fontSize="small" />}        label="Applications"   iconPosition="start" sx={{ fontSize: 12 }} />
      </Tabs>

      <TabPanel value={tab} index={0}><OverviewTab academicYears={academicYears} /></TabPanel>
      <TabPanel value={tab} index={1}><ProgrammesTab academicYears={academicYears} /></TabPanel>
      <TabPanel value={tab} index={2}><AwardsTab academicYears={academicYears} students={students} /></TabPanel>
      <TabPanel value={tab} index={3}><ApplicationsTab academicYears={academicYears} students={students} /></TabPanel>
    </Box>
  );
}
